/**
 * Model lifecycle API client (auto-prepare, enablement, batch).
 * Mirrors the request/error contract of centerApi.ts; no duplicate
 * engine client — every call targets the same canonical endpoints.
 */
import {freshStepUpProof} from '../../lib/identityAccessApi';

type Data=Record<string,any>;

export class LifecycleError extends Error {
  status:number;
  constructor(status:number,message:string){super(message);this.status=status;}
}

async function readError(response:Response):Promise<string>{
  try{
    const data=await response.json();
    if(typeof data?.detail==='string')return data.detail;
  }catch{}
  return 'lifecycle_request_failed_'+response.status;
}

async function post(path:string,body:Data,timeoutMs=25000):Promise<Data>{
  const controller=new AbortController();
  const timer=setTimeout(()=>controller.abort(),timeoutMs);
  try{
    const response=await fetch(path,{
      method:'POST',credentials:'include',cache:'no-store',signal:controller.signal,
      headers:{Accept:'application/json','Content-Type':'application/json'},
      body:JSON.stringify(body),
    });
    if(!response.ok)throw new LifecycleError(response.status,await readError(response));
    return await response.json() as Data;
  }finally{clearTimeout(timer);}
}

async function postVerified(path:string,body:Data,timeoutMs=25000):Promise<Data>{
  const value=await post(path,body,timeoutMs);
  if(value?.updated!==true||!value?.result||typeof value.result!=='object')
    throw new LifecycleError(400,'lifecycle_result_not_verified');
  return value.result as Data;
}

const stepUp=async():Promise<Data>=>{
  const proof=await freshStepUpProof();
  return {
    ceremony_id:proof.ceremony_id,credential:proof.credential,prf_result:proof.prf_result,
  } as Data;
};

const lifecycleBase='/api/v1/harness/provider-settings';

export type LifecycleStage='DISCOVERED'|'PREPARED'|'ENABLED'|'AVAILABLE_IN_HARNESS'|'DISABLED'|'UNSUPPORTED';
export type LifecycleMetadata={
  provenance?:string|null;accuracy?:string|null;tier?:number|null;
  safe_profile?:boolean;real_capacity_known?:boolean;
  context_window?:number|null;max_output_tokens?:number|null;
  real_context_window?:number|null;real_max_output_tokens?:number|null;
  notes?:string[];
};
export type LifecycleEntry={
  upstream_model:string;stage:LifecycleStage;metadata:LifecycleMetadata;
  model_key?:string|null;gateway_enabled?:boolean;
  has_deployment_record?:boolean;deployment_enabled?:boolean|null;
};

export type BatchResult={
  action:string;total:number;executed:number;failed:number;
  results:Data[];
};

export const providerModelLifecycleApi={
  async list(providerKey:string,limit=2000):Promise<{
    provider_key:string;total:number;stage_counts:Record<string,number>;models:LifecycleEntry[];
  }>{
    const value=await post(lifecycleBase+'/models/lifecycle/list/verify',{
      provider_key:providerKey,limit,
    });
    if(!Array.isArray(value.models)||typeof value.total!=='number'||typeof value.provider_key!=='string')
      throw new LifecycleError(400,'lifecycle_list_result_invalid');
    return value as {provider_key:string;total:number;stage_counts:Record<string,number>;models:LifecycleEntry[]};
  },
  async prepareAuto(options:{
    providerKey:string;upstreamModel:string;reason:string;acceptSafeProfile:boolean;
  }){
    return postVerified(lifecycleBase+'/models/gateway/prepare-auto/verify',{
      provider_key:options.providerKey,
      upstream_model:options.upstreamModel,
      reason:options.reason,
      accept_safe_profile:options.acceptSafeProfile,
      ...(await stepUp()),
    });
  },
  async action(options:{
    modelKey:string;action:'ENABLE'|'DISABLE';reason:string;
  }){
    return postVerified(lifecycleBase+'/models/lifecycle/verify',{
      model_key:options.modelKey,action:options.action,reason:options.reason,
      ...(await stepUp()),
    });
  },
  async batch(options:{
    providerKey:string;action:'PREPARE_AUTO'|'ENABLE'|'DISABLE';
    upstreamModels?:string[];modelKeys?:string[];
    reason:string;acceptSafeProfile:boolean;
  }):Promise<BatchResult>{
    const payload:Data={
      provider_key:options.providerKey,action:options.action,
      reason:options.reason,accept_safe_profile:options.acceptSafeProfile,
      ...(await stepUp()),
    };
    if(options.upstreamModels?.length)payload.upstream_models=options.upstreamModels;
    if(options.modelKeys?.length)payload.model_keys=options.modelKeys;
    return await postVerified(
      lifecycleBase+'/models/lifecycle/batch/verify',payload,120000,
    ) as BatchResult;
  },
};
